<template>
  <div class="p-4 pt-2">
    <v-alert color="blue" type="info" outlined dense class="text-sm font-medium mb-4">
      Configure GST Type and Round Off for each charge type applicable to this sub-case.
    </v-alert>

    <!-- Search / filter bar -->
    <v-text-field
      v-model="search"
      prepend-inner-icon="mdi-magnify"
      label="Search charge type…"
      outlined
      dense
      clearable
      class="mb-4"
      hide-details
    />

    <!-- Header row -->
    <div class="tax-header d-flex align-center px-3 py-2 mb-1 rounded">
      <span class="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide"
        >Charge Type</span
      >
      <span class="tax-col text-xs font-semibold text-gray-500 uppercase tracking-wide text-center"
        >GST Type</span
      >
      <span class="tax-col text-xs font-semibold text-gray-500 uppercase tracking-wide text-center"
        >Round Off</span
      >
    </div>

    <!-- Rows -->
    <v-card outlined class="overflow-hidden">
      <div
        v-for="(row, index) in filteredRows"
        :key="row.key"
        class="tax-row d-flex align-center px-3 py-1"
        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
      >
        <span class="flex-1 text-sm text-gray-700 py-1">{{ row.label }}</span>

        <div class="tax-col">
          <v-select
            v-model="row.gstType"
            :items="gstTypeOptions"
            outlined
            dense
            hide-details
            class="compact-select"
          />
        </div>

        <div class="tax-col">
          <v-select
            v-model="row.roundOff"
            :items="roundOffOptions"
            outlined
            dense
            hide-details
            class="compact-select"
          />
        </div>
      </div>

      <div v-if="filteredRows.length === 0" class="pa-6 text-center text-gray-400 text-sm">
        No charge types match your search.
      </div>
    </v-card>
  </div>
</template>

<script>
const DEFAULT_ROWS = [
  { key: 'lpc_interest', label: 'LPC Interest', gstType: 'GST 18', roundOff: 'Inclusive' },
  {
    key: 'loan_amendment',
    label: 'Loan Amendment',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'collection_charges',
    label: 'Collection Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'rto_charges', label: 'RTO Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'insurance', label: 'Insurance', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'seize_charges', label: 'Seize Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'legal_charges', label: 'Legal Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'agreement_charges',
    label: 'Agreement Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'other_charges', label: 'Other Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'bounce_charges', label: 'Bounce Charges', gstType: 'GST 18', roundOff: 'Inclusive' },
  {
    key: 'co_lending_documentation_charges',
    label: 'Co-Lending Documentation Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'processing_fees', label: 'Processing Fees', gstType: 'GST 18', roundOff: 'Inclusive' },
  { key: 'service_tax', label: 'Service Tax', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'stamp_duty', label: 'Stamp Duty', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'dealer_subvention',
    label: 'Dealer Subvention',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'default_charges',
    label: 'Default Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'recovery_charges',
    label: 'Recovery Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'bsp_payout', label: 'BSP Payout', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'foreclosure', label: 'Foreclosure', gstType: 'GST 18', roundOff: 'Exclusive' },
  {
    key: 'part_payment_charges',
    label: 'Part Payment Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'cheque_nach_swapping_charges',
    label: 'Cheque / NACH Swapping Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'cheque_nach_bouncing_charges',
    label: 'Cheque / NACH Bouncing Charges',
    gstType: 'GST 18',
    roundOff: 'Inclusive'
  },
  {
    key: 'first_noc_issuance_charges',
    label: 'First NOC Issuance Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'duplicate_noc_charges',
    label: 'Duplicate NOC Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'loan_re_schedulement_charges',
    label: 'Loan Re-Schedulement Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'valuation_charges',
    label: 'Valuation Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'title_search_report_charges',
    label: 'Title Search Report Charges (Legal Changes)',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'cibil', label: 'CIBIL', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'nach', label: 'NACH', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'statement_of_accounts',
    label: 'Statement Of Accounts',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'late_payment_charges',
    label: 'Late Payment Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'cash_deposition_charges',
    label: 'Cash Deposition Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 're_schedulement_of_emi_date',
    label: 'Re-Schedulement of EMI Date',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'document_deposit_charges',
    label: 'Document Deposit Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'duplicate_repayment_schedule_charges',
    label: 'Duplicate Repayment Schedule Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'inspection_charges',
    label: 'Inspection Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'franking_charges',
    label: 'Franking Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'misc_charges', label: 'Misc Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'mortgage_charges',
    label: 'Mortgage Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'cersai_charges',
    label: 'CERSAI Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'broken_interest',
    label: 'Broken Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'upfront_fees', label: 'Upfront Fees', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'customer_insurance',
    label: 'Customer Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'partial_disbursement_interest',
    label: 'Partial Disbursement Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'membership_card_fee',
    label: 'Membership Card Fee',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'life_insurance_1',
    label: 'Life Insurance 1',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'life_insurance_2',
    label: 'Life Insurance 2',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'hospital_plus_1',
    label: 'Hospital Plus 1',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'hospital_plus_2',
    label: 'Hospital Plus 2',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'other_charges_1',
    label: 'Other Charges 1',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'other_charges_2',
    label: 'Other Charges 2',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'postal_charges',
    label: 'Postal Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'telegram_charges',
    label: 'Telegram Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'pre_payment_charges',
    label: 'Pre-Payment Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'property_insurance',
    label: 'Property Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'custodian_charges',
    label: 'Custodian Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'document_retrieval_charges',
    label: 'Document Retrieval Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'disbursement_cancellation_charges',
    label: 'Disbursement Cancellation Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'additional_interest',
    label: 'Additional Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'advance_interest',
    label: 'Advance Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'third_party_transfer',
    label: 'Third Party Transfer',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'bsp_participation',
    label: 'BSP Participation',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'share_application_money',
    label: 'Share Application Money',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'lpi_od_interest',
    label: 'LPI OD Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'commitment_fee',
    label: 'Commitment Fee',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'moratorium_interest',
    label: 'Moratorium Interest',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'registration_charges',
    label: 'Registration Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'write_back', label: 'Write Back', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'rc_book_charges',
    label: 'RC Book Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'incentive_1', label: 'Incentive 1', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'incentive_2', label: 'Incentive 2', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'commission_1', label: 'Commission 1', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'commission_2', label: 'Commission 2', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'npa_charges', label: 'NPA Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'loan_protection_policy',
    label: 'Loan Protection Policy',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'declaration_cum_affidavit',
    label: 'Declaration Cum Affidavit',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'deposit_of_title_deeds',
    label: 'Deposit Of Title Deeds',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'loan_pre_emi', label: 'Loan Pre EMI', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'device_charges',
    label: 'Device Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'credit_admin_charges',
    label: 'Credit Admin Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'membership_fees',
    label: 'Membership Fees',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'printing_stationary_charges',
    label: 'Printing & Stationary Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'incidental_charges',
    label: 'Incidental Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'sms_charges', label: 'SMS Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'lender_insurance',
    label: 'Lender Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'self_insurance',
    label: 'Self Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'document_charges',
    label: 'Document Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'foreclosure_letter_charges',
    label: 'Foreclosure Letter Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'lod_letter_charges',
    label: 'LOD Letter Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'noc_letter_charges',
    label: 'NOC Letter Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'life_insurance',
    label: 'Life Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'general_insurance',
    label: 'General Insurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'termination_amount_receipt',
    label: 'Termination Amount (Receipt)',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'dealer_commission_payable',
    label: 'Dealer Commission (Payable)',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'loan_suraksha_insurance_r',
    label: 'Loan Suraksha Insurance (R)',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'sale_amount_repossession',
    label: 'Sale Amount (Repossession)',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'interest_subvention_from_oem',
    label: 'Interest Subvention from OEM',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'reschedulement_charges',
    label: 'Reschedulement Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'general_subvention',
    label: 'General Subvention',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'hospi_cash_insurance',
    label: 'HospiCashInsurance',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'life_cycle_management_fee',
    label: 'LifeCycleManagementFee',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'application_fee',
    label: 'Application Fee',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'non_instrumental_charges',
    label: 'Non-Instrumental Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'technical_charges',
    label: 'Technical Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'oem_subvention',
    label: 'OEM Subvention',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'security_deposit',
    label: 'Security Deposit',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'legal_fee', label: 'Legal Fee', gstType: 'Not Available', roundOff: 'Inclusive' },
  { key: 'technical_fee', label: 'Technical Fee', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'tranche_pre_emi',
    label: 'Tranche Pre-EMI',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'late_payment_fee',
    label: 'Late Payment Fee',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  {
    key: 'handling_charges',
    label: 'Handling Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  },
  { key: 'rcu_charges', label: 'RCU Charges', gstType: 'Not Available', roundOff: 'Inclusive' },
  {
    key: 'disbursement_charges',
    label: 'Disbursement Charges',
    gstType: 'Not Available',
    roundOff: 'Inclusive'
  }
]

export default {
  name: 'TaxSettings',

  props: {
    value: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      syncingFromProp: false,
      search: '',
      rows: this.buildRows(this.value),
      gstTypeOptions: ['Not Available', 'GST 18'],
      roundOffOptions: ['Inclusive', 'Exclusive']
    }
  },

  computed: {
    filteredRows() {
      if (!this.search) return this.rows
      const q = this.search.toLowerCase()
      return this.rows.filter((r) => r.label.toLowerCase().includes(q))
    }
  },

  created() {
    this.$emit(
      'input',
      this.rows.map((r) => ({ key: r.key, gstType: r.gstType, roundOff: r.roundOff }))
    )
  },

  watch: {
    value: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.syncingFromProp = true
        this.rows = this.buildRows(val)
        this.$nextTick(() => {
          this.syncingFromProp = false
        })
      }
    },
    rows: {
      deep: true,
      handler(val) {
        if (this.syncingFromProp) return
        this.$emit(
          'input',
          val.map((r) => ({ key: r.key, gstType: r.gstType, roundOff: r.roundOff }))
        )
      }
    }
  },

  methods: {
    buildRows(savedArray) {
      // Start from the full default list, merge in any saved overrides by key
      const savedMap = {}
      if (Array.isArray(savedArray)) {
        savedArray.forEach((s) => {
          if (s && s.key) {
            savedMap[s.key.toLowerCase()] = s
          }
        })
      }
      return DEFAULT_ROWS.map((d) => {
        const saved = savedMap[d.key.toLowerCase()]
        return {
          key: d.key,
          label: d.label,
          gstType:
            saved && saved.gstType !== undefined && saved.gstType !== null
              ? saved.gstType
              : d.gstType,
          roundOff:
            saved && saved.roundOff !== undefined && saved.roundOff !== null
              ? saved.roundOff
              : d.roundOff
        }
      })
    }
  }
}
</script>

<style scoped>
.tax-header {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.tax-col {
  width: 160px;
  min-width: 160px;
  padding: 2px 6px;
}
.tax-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}
.tax-row:last-child {
  border-bottom: none;
}
.tax-row:hover {
  background: #eff6ff !important;
}
.compact-select :deep(.v-input__control) {
  min-height: 36px !important;
}
.compact-select :deep(.v-select__slot) {
  font-size: 13px;
}
</style>
